import React, { useState, useEffect, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import AdminService from "../../../api/services/adminService";
import "../Home/Homepage.css";
import "../Trading/CurrencyPairManagementPage.css";
import "../Trading/AddCurrencyPairPage.css";

const unwrapMongoDecimal = (v) => {
  if (v == null) return "";
  if (typeof v === "object" && v.$numberDecimal != null) return String(v.$numberDecimal);
  return String(v);
};

const RETURN_TYPE_OPTIONS = [
  { value: "daily", label: "Daily" },
  { value: "monthly", label: "Monthly" },
  { value: "yearly", label: "Yearly" },
  { value: "one_time", label: "One Time" },
];

const PAYOUT_STATUS_OPTIONS = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const WITH_PRINCIPAL_AMOUNT_OPTIONS = [
  { value: "false", label: "Without principal" },
  { value: "true", label: "With principal" },
];

const EditStakingPackagePage = () => {
  const navigate = useNavigate();
  const { packageId } = useParams();
  const [loading, setLoading] = useState(true);
  const [coinsList, setCoinsList] = useState([]);
  const [form, setForm] = useState({
    duration_days: "",
    return_percentage: "",
    returnPercenatgeShowtoUser: "",
    return_type: "daily",
    min_amount: "",
    cancellation_fee: "",
    level1_commission: "",
    level1_instant_bonus: "",
    level2_commission: "",
    level3_commission: "",
    level4_commission: "",
    level5_commission: "",
    referral_commission_type: "daily",
    payout_status: "active",
    withPrincipalAmount: "false",
    status: "ACTIVE",
    currencyId: "",
  });
  const [submitLoading, setSubmitLoading] = useState(false);
  const [submitError, setSubmitError] = useState("");
  const [successModal, setSuccessModal] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [loadedLabel, setLoadedLabel] = useState("");

  const fetchCoins = useCallback(async () => {
    const res = await AdminService.getCoinsList();
    if (res?.success && Array.isArray(res.data)) {
      setCoinsList(res.data);
    } else {
      setCoinsList([]);
    }
  }, []);

  useEffect(() => {
    fetchCoins();
  }, [fetchCoins]);

  useEffect(() => {
    const load = async () => {
      if (!packageId) {
        setNotFound(true);
        setLoading(false);
        return;
      }
      setLoading(true);
      setNotFound(false);
      const pkg = await AdminService.fetchEarningPackageById(packageId);
      setLoading(false);
      if (!pkg) {
        setNotFound(true);
        return;
      }
      const cur = pkg.currency ?? pkg.currency_short_name ?? "";
      const fn = pkg.currency_fullname || "";
      setLoadedLabel(fn ? `${cur} (${fn})` : cur);
      const allowedReturnTypes = RETURN_TYPE_OPTIONS.map((o) => o.value);
      const rawReturnType = String(pkg.return_type ?? pkg.returnType ?? "daily").toLowerCase();
      const return_type = allowedReturnTypes.includes(rawReturnType) ? rawReturnType : "daily";
      const rawReferralCommissionType = String(
        pkg.referral_commission_type ?? pkg.referralCommissionType ?? "daily"
      ).toLowerCase();
      const referral_commission_type = allowedReturnTypes.includes(rawReferralCommissionType)
        ? rawReferralCommissionType
        : "daily";
      const allowedPayoutStatuses = PAYOUT_STATUS_OPTIONS.map((o) => o.value);
      const rawPayoutStatus = String(pkg.payout_status ?? "active").toLowerCase();
      const payout_status = allowedPayoutStatuses.includes(rawPayoutStatus)
        ? rawPayoutStatus
        : "active";
      const withPrincipalRaw = pkg.withPrincipalAmount ?? pkg.with_principal_amount;
      const withPrincipalAmount =
        withPrincipalRaw === true || withPrincipalRaw === 1 || withPrincipalRaw === "true"
          ? "true"
          : "false";
      setForm({
        duration_days: pkg.duration_days ?? "",
        return_percentage: pkg.return_percentage ?? "",
        returnPercenatgeShowtoUser:
          pkg.returnPercenatgeShowtoUser != null && pkg.returnPercenatgeShowtoUser !== ""
            ? String(pkg.returnPercenatgeShowtoUser)
            : "",
        return_type,
        min_amount: unwrapMongoDecimal(pkg.min_amount),
        cancellation_fee: unwrapMongoDecimal(pkg.cancellation_fee ?? pkg.cancellationFee),
        level1_commission:
          pkg.level1_commission != null && pkg.level1_commission !== ""
            ? String(pkg.level1_commission)
            : "",
        level1_instant_bonus:
          pkg.level1_instant_bonus != null && pkg.level1_instant_bonus !== ""
            ? String(pkg.level1_instant_bonus)
            : "",
        level2_commission:
          pkg.level2_commission != null && pkg.level2_commission !== ""
            ? String(pkg.level2_commission)
            : "",
        level3_commission:
          pkg.level3_commission != null && pkg.level3_commission !== ""
            ? String(pkg.level3_commission)
            : "",
        level4_commission:
          pkg.level4_commission != null && pkg.level4_commission !== ""
            ? String(pkg.level4_commission)
            : "",
        level5_commission:
          pkg.level5_commission != null && pkg.level5_commission !== ""
            ? String(pkg.level5_commission)
            : "",
        referral_commission_type,
        payout_status,
        withPrincipalAmount,
        status: String(pkg.status || "ACTIVE").toUpperCase() === "INACTIVE" ? "INACTIVE" : "ACTIVE",
        currencyId: pkg.currency_id || pkg.currencyId || "",
      });
    };
    load();
  }, [packageId]);

  const updateForm = (key, value) => {
    setSubmitError("");
    setForm((f) => ({ ...f, [key]: value }));
  };

  const handleSubmit = async (e) => {
    e?.preventDefault?.();
    setSubmitError("");

    const duration_days = Number(form.duration_days);
    const return_percentage = Number(form.return_percentage);
    const returnPercenatgeShowtoUser = Number(form.returnPercenatgeShowtoUser);
    const min_amount = Number(form.min_amount);
    const cancellation_fee =
      form.cancellation_fee === "" || form.cancellation_fee == null
        ? 0
        : Number(form.cancellation_fee);
    const level1_commission = Number(form.level1_commission);
    const level1_instant_bonus = Number(form.level1_instant_bonus);
    const level2_commission = Number(form.level2_commission);
    const level3_commission = Number(form.level3_commission);
    const level4_commission = Number(form.level4_commission);
    const level5_commission = Number(form.level5_commission);

    if (!Number.isFinite(duration_days) || duration_days <= 0) {
      setSubmitError("Duration (days) must be greater than 0.");
      return;
    }
    if (!Number.isFinite(return_percentage) || return_percentage <= 0) {
      setSubmitError("Return (%) must be greater than 0.");
      return;
    }
    if (!Number.isFinite(returnPercenatgeShowtoUser) || returnPercenatgeShowtoUser <= 0) {
      setSubmitError("Return % show to user must be greater than 0.");
      return;
    }
    const allowedReturnTypes = RETURN_TYPE_OPTIONS.map((o) => o.value);
    if (!allowedReturnTypes.includes(form.return_type)) {
      setSubmitError("Select a valid return type.");
      return;
    }
    if (!Number.isFinite(min_amount) || min_amount <= 0) {
      setSubmitError("Minimum amount must be greater than 0.");
      return;
    }
    if (!Number.isFinite(cancellation_fee) || cancellation_fee < 0) {
      setSubmitError("Cancellation fee must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level1_commission) || level1_commission < 0) {
      setSubmitError("Level 1 commission must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level1_instant_bonus) || level1_instant_bonus < 0) {
      setSubmitError("Level 1 instant bonus must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level2_commission) || level2_commission < 0) {
      setSubmitError("Level 2 commission must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level3_commission) || level3_commission < 0) {
      setSubmitError("Level 3 commission must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level4_commission) || level4_commission < 0) {
      setSubmitError("Level 4 commission must be 0 or greater.");
      return;
    }
    if (!Number.isFinite(level5_commission) || level5_commission < 0) {
      setSubmitError("Level 5 commission must be 0 or greater.");
      return;
    }
    const allowedReferralCommissionTypes = RETURN_TYPE_OPTIONS.map((o) => o.value);
    if (!allowedReferralCommissionTypes.includes(form.referral_commission_type)) {
      setSubmitError("Select a valid referral commission type.");
      return;
    }
    const allowedPayoutStatuses = PAYOUT_STATUS_OPTIONS.map((o) => o.value);
    if (!allowedPayoutStatuses.includes(form.payout_status)) {
      setSubmitError("Select a valid payout status.");
      return;
    }
    const allowedWithPrincipal = WITH_PRINCIPAL_AMOUNT_OPTIONS.map((o) => o.value);
    if (!allowedWithPrincipal.includes(form.withPrincipalAmount)) {
      setSubmitError("Select a valid principal amount option.");
      return;
    }

    const body = {
      duration_days,
      return_percentage,
      returnPercenatgeShowtoUser,
      return_type: form.return_type,
      min_amount,
      cancellation_fee,
      level1_commission,
      level1_instant_bonus,
      level2_commission,
      level3_commission,
      level4_commission,
      level5_commission,
      referral_commission_type: form.referral_commission_type,
      payout_status: form.payout_status,
      withPrincipalAmount: form.withPrincipalAmount === "true",
      status: form.status,
    };
    if (form.currencyId?.trim()) {
      body.currencyId = form.currencyId.trim();
    }

    setSubmitLoading(true);
    try {
      const res = await AdminService.updateEarningPackage(packageId, body);
      if (res?.success) {
        setSuccessModal(true);
      } else {
        setSubmitError(res?.message || "Could not update package.");
      }
    } catch (err) {
      console.error(err);
      setSubmitError("Could not update package.");
    } finally {
      setSubmitLoading(false);
    }
  };

  const handleSuccessClose = () => {
    setSuccessModal(false);
    navigate("/dashboard/staking-packages");
  };

  if (notFound) {
    return (
      <main className="dashboard-main dashboard-subpage">
        <section className="dashboard-hero">
          <div className="dashboard-hero__text">
            <p className="dashboard-hero__breadcrumb">
              <Link to="/dashboard/homepage">Dashboard</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <Link to="/dashboard/staking-packages">Staking</Link>
              <span style={{ margin: "0 0.35rem" }}>/</span>
              <span>Edit Package</span>
            </p>
            <h1 className="dashboard-hero__title">Package Not Found</h1>
          </div>
        </section>
        <div style={{ padding: "2rem", textAlign: "center" }}>
          <p>
            Package not found. <Link to="/dashboard/staking-packages">Back to Packages</Link>
          </p>
        </div>
      </main>
    );
  }

  return (
    <main className="dashboard-main dashboard-subpage addpair-page">
      <section className="dashboard-hero">
        <div className="dashboard-hero__text">
          <p className="dashboard-hero__breadcrumb">
            <Link to="/dashboard/homepage">Dashboard</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <Link to="/dashboard/staking-packages">Staking</Link>
            <span style={{ margin: "0 0.35rem" }}>/</span>
            <span>Edit Package</span>
          </p>
          <h1 className="dashboard-hero__title">Edit Staking Package</h1>
          <p className="dashboard-hero__subtitle">
            {loading ? "Loading…" : loadedLabel || "Update earning package"}
          </p>
        </div>
        <div className="dashboard-hero__icon addpair-hero__icon">
          <i className="fas fa-edit" />
        </div>
      </section>

      <div className="dashboard-body addpair-body">
        <div className="addpair-card">
          {submitError && (
            <p
              style={{
                color: "#dc2626",
                margin: "0 0 1rem",
                padding: "0.5rem 0.75rem",
                background: "rgba(220, 38, 38, 0.08)",
                borderRadius: 8,
              }}
            >
              {submitError}
            </p>
          )}
          {loading ? (
            <p style={{ padding: "2rem", textAlign: "center" }}>
              <i className="fas fa-spinner fa-spin" /> Loading package…
            </p>
          ) : (
            <form onSubmit={handleSubmit} className="addpair-form">
              <div className="addpair-config-grid">
                <div className="cp-field">
                  <label>Currency (change)</label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-gem" />
                    <select
                      value={form.currencyId}
                      onChange={(e) => updateForm("currencyId", e.target.value)}
                    >
                      <option value="">Keep current currency</option>
                      {[...coinsList]
                        .filter((c) => c._id || c.id)
                        .sort((a, b) =>
                          (a.short_name || "").localeCompare(b.short_name || "")
                        )
                        .map((c) => {
                          const id = c._id || c.id;
                          const label = c.short_name || c.name || id;
                          return (
                            <option key={id} value={id}>
                              {label}
                              {c.name && c.name !== label ? ` — ${c.name}` : ""}
                            </option>
                          );
                        })}
                    </select>
                  </div>
                </div>
                <div className="cp-field">
                  <label>
                    Duration (days) <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-calendar-day" />
                    <input
                      type="number"
                      step="1"
                      min={1}
                      value={form.duration_days}
                      onChange={(e) => updateForm("duration_days", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="cp-field">
                  <label>
                    Return (%) <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-percent" />
                    <input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={form.return_percentage}
                      onChange={(e) => updateForm("return_percentage", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="cp-field">
                  <label>
                    Return % show to user <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-eye" />
                    <input
                      type="number"
                      step="0.01"
                      min={0.01}
                      value={form.returnPercenatgeShowtoUser}
                      onChange={(e) =>
                        updateForm("returnPercenatgeShowtoUser", e.target.value)
                      }
                      required
                    />
                  </div>
                  <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                    Return percentage displayed to users.
                  </small>
                </div>
                <div className="cp-field">
                  <label>
                    Return type <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-clock" />
                    <select
                      value={form.return_type}
                      onChange={(e) => updateForm("return_type", e.target.value)}
                      required
                    >
                      {RETURN_TYPE_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                    How often return % applies (daily, monthly, yearly, or one time).
                  </small>
                </div>
                <div className="cp-field">
                  <label>
                    Min amount <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-coins" />
                    <input
                      type="number"
                      step="any"
                      min={0.00000001}
                      value={form.min_amount}
                      onChange={(e) => updateForm("min_amount", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="cp-field">
                  <label>Cancellation fee</label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-ban" />
                    <input
                      type="number"
                      step="any"
                      min={0}
                      placeholder="0"
                      value={form.cancellation_fee}
                      onChange={(e) => updateForm("cancellation_fee", e.target.value)}
                    />
                  </div>
                  <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                    Early cancel fee (0 if none).
                  </small>
                </div>
                <div className="cp-field">
                  <label>Referral commission type</label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-sync-alt" />
                    <select
                      value={form.referral_commission_type}
                      onChange={(e) =>
                        updateForm("referral_commission_type", e.target.value)
                      }
                    >
                      {RETURN_TYPE_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                    How often referral commission is paid.
                  </small>
                </div>
                <div className="cp-field">
                  <label>
                    Level 1 commission (%) <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-user-friends" />
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={form.level1_commission}
                      onChange={(e) => updateForm("level1_commission", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="cp-field">
                  <label>
                    Level 1 instant bonus (%) <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-bolt" />
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={form.level1_instant_bonus}
                      onChange={(e) => updateForm("level1_instant_bonus", e.target.value)}
                      required
                    />
                  </div>
                  <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                    Instant bonus for level 1 (0 if none).
                  </small>
                </div>
                <div className="cp-field">
                  <label>
                    Level 2 commission (%) <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-users" />
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={form.level2_commission}
                      onChange={(e) => updateForm("level2_commission", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="cp-field">
                  <label>
                    Level 3 commission (%) <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-sitemap" />
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={form.level3_commission}
                      onChange={(e) => updateForm("level3_commission", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="cp-field">
                  <label>
                    Level 4 commission (%) <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-sitemap" />
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={form.level4_commission}
                      onChange={(e) => updateForm("level4_commission", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="cp-field">
                  <label>
                    Level 5 commission (%) <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-sitemap" />
                    <input
                      type="number"
                      step="0.01"
                      min={0}
                      value={form.level5_commission}
                      onChange={(e) => updateForm("level5_commission", e.target.value)}
                      required
                    />
                  </div>
                </div>
                <div className="cp-field">
                  <label>
                    Principal amount <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-wallet" />
                    <select
                      value={form.withPrincipalAmount}
                      onChange={(e) => updateForm("withPrincipalAmount", e.target.value)}
                      required
                    >
                      {WITH_PRINCIPAL_AMOUNT_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                  <small style={{ opacity: 0.75, display: "block", marginTop: 4 }}>
                    With principal includes staked amount in payout; without principal pays returns only.
                  </small>
                </div>
                <div className="cp-field">
                  <label>
                    Payout status <span className="required">*</span>
                  </label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-hand-holding-usd" />
                    <select
                      value={form.payout_status}
                      onChange={(e) => updateForm("payout_status", e.target.value)}
                      required
                    >
                      {PAYOUT_STATUS_OPTIONS.map(({ value, label }) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="cp-field">
                  <label>Status</label>
                  <div className="cp-input-wrap">
                    <i className="fas fa-toggle-on" />
                    <select
                      value={form.status}
                      onChange={(e) => updateForm("status", e.target.value)}
                    >
                      <option value="ACTIVE">ACTIVE</option>
                      <option value="INACTIVE">INACTIVE</option>
                    </select>
                  </div>
                </div>
              </div>
              <div className="addpair-actions">
                <Link to="/dashboard/staking-packages" className="cp-btn cp-btn--outline">
                  Cancel
                </Link>
                <button type="submit" className="cp-btn cp-btn--primary" disabled={submitLoading}>
                  {submitLoading ? (
                    <>
                      <i className="fas fa-spinner fa-spin" /> Saving…
                    </>
                  ) : (
                    <>
                      <i className="fas fa-save" /> Save changes
                    </>
                  )}
                </button>
              </div>
            </form>
          )}
        </div>
      </div>

      {successModal && (
        <div className="addpair-success-overlay" onClick={handleSuccessClose}>
          <div className="addpair-success-modal" onClick={(e) => e.stopPropagation()}>
            <div className="addpair-success-icon">
              <i className="fas fa-check-circle" />
            </div>
            <h3>Package updated</h3>
            <p>Earning package has been updated successfully.</p>
            <button type="button" className="cp-btn cp-btn--primary" onClick={handleSuccessClose}>
              Done
            </button>
          </div>
        </div>
      )}
    </main>
  );
};

export default EditStakingPackagePage;
